import { useState } from 'react';
import { useAuth } from '../hooks/useAuth';

const s = {
  page: { minHeight: '100vh', background: 'linear-gradient(135deg, #0a0c15 0%, #0f1a2e 50%, #0a0c15 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px', fontFamily: "'Sora', sans-serif", position: 'relative', overflow: 'hidden' },
  glow1: { position: 'absolute', top: '-200px', left: '-200px', width: 600, height: 600, background: 'radial-gradient(circle, rgba(59,130,246,0.12) 0%, transparent 70%)', pointerEvents: 'none' },
  glow2: { position: 'absolute', bottom: '-200px', right: '-200px', width: 600, height: 600, background: 'radial-gradient(circle, rgba(139,92,246,0.1) 0%, transparent 70%)', pointerEvents: 'none' },
  card: { background: 'rgba(16,20,40,0.95)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 20, padding: '48px 40px', width: '100%', maxWidth: 440, boxShadow: '0 32px 80px rgba(0,0,0,0.6)', backdropFilter: 'blur(20px)', position: 'relative', zIndex: 1 },
  logo: { textAlign: 'center', marginBottom: 32 },
  logoIcon: { fontSize: 48, marginBottom: 8 },
  logoText: { fontSize: 28, fontWeight: 800, background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', letterSpacing: '-0.5px' },
  logoSub: { fontSize: 13, color: '#4a5568', marginTop: 4 },
  tabs: { display: 'flex', background: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: 4, marginBottom: 28, gap: 4 },
  tab: (active) => ({ flex: 1, padding: '10px', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600, fontFamily: "'Sora', sans-serif", transition: 'all 0.2s', background: active ? 'linear-gradient(135deg, #3b82f6, #6366f1)' : 'transparent', color: active ? '#fff' : '#666' }),
  label: { fontSize: 12, color: '#6b7280', marginBottom: 6, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase' },
  input: { width: '100%', padding: '12px 16px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, color: '#e8e8f0', fontSize: 14, fontFamily: "'Sora', sans-serif", outline: 'none', marginBottom: 16, transition: 'border 0.2s' },
  btn: { width: '100%', padding: '14px', background: 'linear-gradient(135deg, #3b82f6, #6366f1)', color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, fontFamily: "'Sora', sans-serif", cursor: 'pointer', marginTop: 8, letterSpacing: 0.3 },
  error: { background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '10px 14px', color: '#f87171', fontSize: 13, marginBottom: 16 },
  features: { marginTop: 32, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 },
  feat: { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8, padding: '10px 12px', fontSize: 12, color: '#9ca3af' },
  featIcon: { fontSize: 18, marginBottom: 4 },
};

export default function AuthPage() {
  const { login, signup } = useAuth();
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async () => {
    setError(''); setLoading(true);
    try {
      if (mode === 'login') await login(form.email, form.password);
      else await signup(form.name, form.email, form.password);
    } catch (e) {
      setError(e.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={s.page}>
      <div style={s.glow1} /><div style={s.glow2} />
      <div style={s.card}>
        <div style={s.logo}>
          <div style={s.logoIcon}>📄</div>
          <div style={s.logoText}>FreePDFMaster.in</div>
          <div style={s.logoSub}>Professional PDF Editing, Free Online</div>
        </div>

        <div style={s.tabs}>
          <button style={s.tab(mode === 'login')} onClick={() => { setMode('login'); setError(''); }}>Sign In</button>
          <button style={s.tab(mode === 'signup')} onClick={() => { setMode('signup'); setError(''); }}>Create Account</button>
        </div>

        {error && <div style={s.error}>{error}</div>}

        {mode === 'signup' && (
          <><div style={s.label}>Full Name</div>
          <input style={s.input} placeholder="John Smith" value={form.name} onChange={set('name')} /></>
        )}
        <div style={s.label}>Email</div>
        <input style={s.input} type="email" placeholder="you@email.com" value={form.email} onChange={set('email')} />
        <div style={s.label}>Password</div>
        <input style={s.input} type="password" placeholder="••••••••" value={form.password} onChange={set('password')}
          onKeyDown={(e) => e.key === 'Enter' && handleSubmit()} />

        <button style={s.btn} onClick={handleSubmit} disabled={loading}>
          {loading ? 'Please wait…' : mode === 'login' ? '→ Sign In' : '→ Create Free Account'}
        </button>

        <div style={s.features}>
          {[['✏️', 'Edit Text'], ['🖼️', 'Insert Images'], ['🗑️', 'Delete Content'], ['🔒', 'Lock Docs']].map(([ic, lb]) => (
            <div key={lb} style={s.feat}><div style={s.featIcon}>{ic}</div>{lb}</div>
          ))}
        </div>

        <div style={{ marginTop: 20, textAlign: 'center', fontSize: 11, color: '#374151' }}>
          Free: 5 exports/month &nbsp;·&nbsp; No credit card needed
        </div>
      </div>
    </div>
  );
}
